import type {
  RevenueMetrics,
  RevenueTimeseriesPoint,
  RevenueAccountRow,
  RevenueByDimensionRow,
  RevenueAttributionModel,
  RevenueLtvCohort,
  RevenueTransactionRow,
} from '@logspot-platform/platform-types';
import { api } from './api-client';

// Account-first revenue dashboard API. Session auth + owner/admin + plan-gated
// on the backend (403 on a plan without revenue analytics). Every report is
// scoped to a project (its reserved revenue event name) over a date range and
// returns the project's base currency alongside the rows so the UI formats
// money in the right currency. Backend wraps payloads in SuccessResponse({ data
// }); we unwrap `.data`.
type SuccessEnvelope<T> = { data: T };

type Range = { startDate?: string; endDate?: string };

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export type RevenueAttributionLevel = 'account' | 'identity';
export type RevenueAttributionDimension =
  | 'source'
  | 'medium'
  | 'campaign'
  | 'channel';
export type RevenueProperty = 'product' | 'plan';

export const RevenueService = {
  getMetrics: async (
    projectId: string,
    range: Range = {},
  ): Promise<RevenueMetrics> => {
    const result = await api.get<SuccessEnvelope<RevenueMetrics>>(
      `/revenue/metrics${qs({ projectId, ...range })}`,
    );
    return result.data;
  },

  getTimeseries: async (
    projectId: string,
    range: Range = {},
  ): Promise<{ series: RevenueTimeseriesPoint[]; currency: string }> => {
    const result = await api.get<
      SuccessEnvelope<{ series: RevenueTimeseriesPoint[]; currency: string }>
    >(`/revenue/timeseries${qs({ projectId, ...range })}`);
    return result.data;
  },

  getTopAccounts: async (
    projectId: string,
    params: Range & { limit?: number } = {},
  ): Promise<{ accounts: RevenueAccountRow[]; currency: string }> => {
    const result = await api.get<
      SuccessEnvelope<{ accounts: RevenueAccountRow[]; currency: string }>
    >(`/revenue/top-accounts${qs({ projectId, ...params })}`);
    return result.data;
  },

  getByAttribution: async (
    projectId: string,
    params: Range & {
      model: RevenueAttributionModel;
      level: RevenueAttributionLevel;
      dimension: RevenueAttributionDimension;
      limit?: number;
    },
  ): Promise<{ rows: RevenueByDimensionRow[]; currency: string }> => {
    const result = await api.get<
      SuccessEnvelope<{ rows: RevenueByDimensionRow[]; currency: string }>
    >(`/revenue/by-attribution${qs({ projectId, ...params })}`);
    return result.data;
  },

  getByProperty: async (
    projectId: string,
    params: Range & { property: RevenueProperty; limit?: number },
  ): Promise<{ rows: RevenueByDimensionRow[]; currency: string }> => {
    const result = await api.get<
      SuccessEnvelope<{ rows: RevenueByDimensionRow[]; currency: string }>
    >(`/revenue/by-property${qs({ projectId, ...params })}`);
    return result.data;
  },

  getTransactions: async (
    projectId: string,
    params: Range & { limit?: number } = {},
  ): Promise<{ transactions: RevenueTransactionRow[]; currency: string }> => {
    const result = await api.get<
      SuccessEnvelope<{ transactions: RevenueTransactionRow[]; currency: string }>
    >(`/revenue/transactions${qs({ projectId, ...params })}`);
    return result.data;
  },

  getLtv: async (
    projectId: string,
    params: { level: RevenueAttributionLevel; limit?: number },
  ): Promise<{ cohorts: RevenueLtvCohort[]; currency: string }> => {
    const result = await api.get<
      SuccessEnvelope<{ cohorts: RevenueLtvCohort[]; currency: string }>
    >(`/revenue/ltv${qs({ projectId, ...params })}`);
    return result.data;
  },
};
